import { removeDuplicates } from '@/core/lib/array';
import type { BetBuilderMarketTypeInfo } from '@/core/lib/betBuilderTypeConverter';
import { marketTypeToBetBuilder } from '@/core/lib/betBuilderTypeConverter';
import { i18n } from '@/core/lib/i18n';
import { isFirstHalfBaseball, isFirstHalfBaseballMarket, isFullTimeMainMarket, isHighestOpeningPartnership, isNextGoal, isOU, nextGoalMarketGroups } from '@/core/lib/oddsHelper';
import { isMobile } from '@/core/lib/utils';
import type { DoubleLineMarketTypeConfig, MarketColumnEntry, SingleLineMarketTypeConfig } from '@/interface/IMarketTypeInfo';
import { BetTypeGroup, MarketType, MarketTypeHeader, OddsDisplayBlockType, OddsDisplayMode, SportType } from '@/interface/enum';

const SingleLineMainMarketConfig: SingleLineMarketTypeConfig = {
    default: [
        { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
        [{ marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder }, { marketGroup: BetTypeGroup.Soccer_PenaltyTenKick, marketType: MarketType.OverUnder }],
        { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
        { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
    ],
};

const DoubleLineMainMarketConfig: DoubleLineMarketTypeConfig = {
    [SportType.Soccer]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.LiveScore },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            [{ marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder }, { marketGroup: BetTypeGroup.Soccer_PenaltyTenKick, marketType: MarketType.OverUnder }],
            [{ marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 }, { marketGroup: BetTypeGroup.Soccer_PenaltyTenKick, marketType: MarketType._1X2 }],
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_1X2 },
            { marketGroup: nextGoalMarketGroups[0], marketType: MarketType.Handicap }, // Next goal
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            [{ marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder }, { marketGroup: BetTypeGroup.Soccer_PenaltyTenKick, marketType: MarketType.OverUnder }],
            [{ marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 }, { marketGroup: BetTypeGroup.Soccer_PenaltyTenKick, marketType: MarketType._1X2 }],
            [{ marketGroup: BetTypeGroup.Main, marketType: MarketType.OddEven }, { marketGroup: BetTypeGroup.Soccer_PenaltyTenKick, marketType: MarketType.OddEven }],
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_1X2 },
        ],
    },
    [SportType.Cricket]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Cricket_HighestOpeningPartnership, marketType: MarketType._1X2 },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Cricket_HighestOpeningPartnership, marketType: MarketType._1X2 },
        ],
    },
    [SportType.Tennis]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Tennis_GameHandicap, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Tennis_GameHandicap, marketType: MarketType.OverUnder },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Tennis_GameHandicap, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Tennis_GameHandicap, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Tennis_GameHandicap, marketType: MarketType.OddEven },
        ],
    },
    [SportType.TableTennis]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.TableTennis_TotalMatchPoints, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.TableTennis_TotalMatchPoints, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.TableTennis_TotalMatchPoints, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.TableTennis_TotalMatchPoints, marketType: MarketType.OddEven },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.TableTennis_TotalMatchPoints, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.TableTennis_TotalMatchPoints, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.TableTennis_TotalMatchPoints, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.TableTennis_TotalMatchPoints, marketType: MarketType.OddEven },
        ],
        newBetTypeLive: {
            [BetTypeGroup.TableTennis_FirstSetPointHandicap]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_FirstSetPointHandicap, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_FirstSetPointHandicap, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_FirstSetPointHandicap, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_FirstSetPointHandicap, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_SecondSetPointHandicap]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_SecondSetPointHandicap, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_SecondSetPointHandicap, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_SecondSetPointHandicap, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_SecondSetPointHandicap, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_ThirdSetPointHandicap]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetPointHandicap, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetPointHandicap, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetPointHandicap, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetPointHandicap, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_FourthSetPointHandicap]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_FourthSetPointHandicap, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_FourthSetPointHandicap, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_FourthSetPointHandicap, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_FourthSetPointHandicap, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_FifthSetPointHandicap]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_FifthSetPointHandicap, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_FifthSetPointHandicap, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_FifthSetPointHandicap, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_FifthSetPointHandicap, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_SixthSetPointHandicap]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_SixthSetPointHandicap, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_SixthSetPointHandicap, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_SixthSetPointHandicap, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_SixthSetPointHandicap, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_SeventhSetPointHandicap]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetPointHandicap, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetPointHandicap, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetPointHandicap, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetPointHandicap, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_SecondSetAwayTeamTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_SecondSetAwayTeamTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_SecondSetAwayTeamTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_SecondSetAwayTeamTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_SecondSetAwayTeamTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_SecondSetHomePlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_SecondSetHomePlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_SecondSetHomePlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_SecondSetHomePlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_SecondSetHomePlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_FirstSetHomePlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_FirstSetHomePlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_FirstSetHomePlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_FirstSetHomePlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_FirstSetHomePlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_FirstSetAwayPlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_FirstSetAwayPlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_FirstSetAwayPlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_FirstSetAwayPlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_FirstSetAwayPlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_ThirdSetHomePlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetHomePlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetHomePlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetHomePlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetHomePlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_FourthSetHomePlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_FourthSetHomePlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_FourthSetHomePlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_FourthSetHomePlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_FourthSetHomePlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_FifthSetHomePlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_FifthSetHomePlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_FifthSetHomePlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_FifthSetHomePlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_FifthSetHomePlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_SixthSetHomePlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_SixthSetHomePlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_SixthSetHomePlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_SixthSetHomePlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_SixthSetHomePlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_SeventhSetHomePlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetHomePlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetHomePlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetHomePlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetHomePlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_ThirdSetAwayPlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetAwayPlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetAwayPlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetAwayPlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_ThirdSetAwayPlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_FourthSetAwayPlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_FourthSetAwayPlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_FourthSetAwayPlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_FourthSetAwayPlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_FourthSetAwayPlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_FifthSetAwayPlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_FifthSetAwayPlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_FifthSetAwayPlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_FifthSetAwayPlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_FifthSetAwayPlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_SixthSetAwayPlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_SixthSetAwayPlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_SixthSetAwayPlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_SixthSetAwayPlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_SixthSetAwayPlayerTotalPoints, marketType: MarketType.OddEven },
            ],
            [BetTypeGroup.TableTennis_SeventhSetAwayPlayerTotalPoints]: [
                null,
                null,
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetAwayPlayerTotalPoints, marketType: MarketType.MoneyLine },
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetAwayPlayerTotalPoints, marketType: MarketType.Handicap },
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetAwayPlayerTotalPoints, marketType: MarketType.OverUnder },
                { marketGroup: BetTypeGroup.TableTennis_SeventhSetAwayPlayerTotalPoints, marketType: MarketType.OddEven },
            ],
        },
    },
    [SportType.Badminton]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Badminton_TotalMatchPoints, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Badminton_TotalMatchPoints, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Badminton_TotalMatchPoints, marketType: MarketType.OddEven },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Badminton_TotalMatchPoints, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Badminton_TotalMatchPoints, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Badminton_TotalMatchPoints, marketType: MarketType.OddEven },
        ],
    },
    [SportType.Volleyball]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Volleyball_TotalMatchPoints, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Volleyball_TotalMatchPoints, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Volleyball_TotalMatchPoints, marketType: MarketType.OddEven },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Volleyball_TotalMatchPoints, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Volleyball_TotalMatchPoints, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Volleyball_TotalMatchPoints, marketType: MarketType.OddEven },
        ],
    },
    [SportType.Basketball]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OddEven },
            { marketGroup: BetTypeGroup.Basketball_FirstHalfMoneyLine, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OddEven },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OddEven },
            { marketGroup: BetTypeGroup.Basketball_FirstHalfMoneyLine, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OddEven },
        ],
    },
    [SportType.Football]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OddEven },
            { marketGroup: BetTypeGroup.AmericanFootball_FirstHalfMoneyLine, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OddEven },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OddEven },
            { marketGroup: BetTypeGroup.AmericanFootball_FirstHalfMoneyLine, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OddEven },
        ],
    },
    [SportType.IceHockey]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OddEven },
            { marketGroup: BetTypeGroup.Hockey_FirstPeriod, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Hockey_FirstPeriod, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Hockey_FirstPeriod, marketType: MarketType.OverUnder },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Hockey_FirstPeriod, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Hockey_FirstPeriod, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Hockey_FirstPeriod, marketType: MarketType.OverUnder },
        ],
    },
    [SportType.GeneralHockey]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OddEven },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OddEven },
        ],
    },
    [SportType.Futsal]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Futsal_FirstHalf, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Futsal_FirstHalf, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Futsal_FirstHalf, marketType: MarketType.OverUnder },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Futsal_FirstHalf, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Futsal_FirstHalf, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Futsal_FirstHalf, marketType: MarketType.OverUnder },
        ],
    },
    [SportType.Baseball]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
            { marketGroup: BetTypeGroup.Baseball_FirstInningRuns, marketType: MarketType._1X2 },
        ],
    },
    [SportType.BeachSoccer]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OddEven },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OddEven },
        ],
    },
    [SportType.WaterPolo]: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType._1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_1X2 },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
        ],
    },

    default: {
        live: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
        ],
        nonLive: [
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.MoneyLine },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.OddEven },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_Handicap },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OverUnder },
            { marketGroup: BetTypeGroup.Main, marketType: MarketType.FH_OddEven },
        ],
    },
};

export const OddsDisplayBlockTypeToColumnWidthMapping: Partial<Record<OddsDisplayBlockType, number>> = {
    [OddsDisplayBlockType.Time]: 66,
    [OddsDisplayBlockType.Team]: 180,
    [OddsDisplayBlockType.More]: 50,
};

export const MarketTypeToColumnWidthMapping: Record<MarketType, number> = {
    [MarketType.Unknown]: 0,
    [MarketType.Handicap]: 90,
    [MarketType.OddEven]: 72,
    [MarketType.OverUnder]: 108,
    [MarketType.CorrectScore]: 0,
    [MarketType._1X2]: 64,
    [MarketType.TotalGoal]: 0,
    [MarketType.FH_Handicap]: 90,
    [MarketType.FH_1X2]: 64,
    [MarketType.FH_OverUnder]: 108,
    [MarketType.HTFT]: 0,
    [MarketType.MoneyLine]: 64,
    [MarketType.FH_OddEven]: 72,
    [MarketType.FGLG]: 0,
    [MarketType.FH_CorrectScore]: 0,
    [MarketType.SH_CorrectScore]: 0,
    [MarketType.DoubleChance]: 0,
    [MarketType.LiveScore]: 60,
    [MarketType.FH_LiveScore]: 40,
    [MarketType.Game]: 0,
    [MarketType.OutRight]: 0,
    [MarketType.MixParlay]: 0,
    [MarketType.BetBuilder]: 0,
    [MarketType.Dynamic]: 0,
};

export const MarketGroupToColumnWidthMapping: Partial<Record<BetTypeGroup, number>> = {
    [BetTypeGroup.Soccer_FirstGoal]: 50,
    [BetTypeGroup.Soccer_SecondGoal]: 50,
    [BetTypeGroup.Soccer_ThirdGoal]: 50,
    [BetTypeGroup.Soccer_ForthGoal]: 50,
    [BetTypeGroup.Soccer_FifthGoal]: 50,
    [BetTypeGroup.Soccer_SixthGoal]: 50,
    [BetTypeGroup.Soccer_SeventhGoal]: 50,
    [BetTypeGroup.Soccer_EighthGoal]: 50,
    [BetTypeGroup.Soccer_NinthGoal]: 50,
    [BetTypeGroup.Soccer_TenthGoal]: 50,
    [BetTypeGroup.Cricket_HighestOpeningPartnership]: 126,
};

export const MarketToColumnWidthLimitMapping = new Map<BetTypeGroup, Map<MarketType, number>>([
    [BetTypeGroup.Cricket_HighestOpeningPartnership, new Map([
        [MarketType._1X2, 80],
    ])],
]);

export const MarketTypeGroupingMapping = new Map<MarketType, OddsDisplayBlockType>([
    [MarketType.Handicap, OddsDisplayBlockType.FullTime],
    [MarketType._1X2, OddsDisplayBlockType.FullTime],
    [MarketType.OverUnder, OddsDisplayBlockType.FullTime],
    [MarketType.OddEven, OddsDisplayBlockType.FullTime],
    [MarketType.CorrectScore, OddsDisplayBlockType.FullTime],
    [MarketType.LiveScore, OddsDisplayBlockType.FullTime],
    [MarketType.MoneyLine, OddsDisplayBlockType.FullTime],
    [MarketType.FH_Handicap, OddsDisplayBlockType.FirstHalf],
    [MarketType.FH_1X2, OddsDisplayBlockType.FirstHalf],
    [MarketType.FH_OverUnder, OddsDisplayBlockType.FirstHalf],
    [MarketType.FH_OddEven, OddsDisplayBlockType.FirstHalf],
    [MarketType.FH_CorrectScore, OddsDisplayBlockType.FirstHalf],
    [MarketType.FH_LiveScore, OddsDisplayBlockType.FirstHalf],
]);

export const NewBetTypeGroupingMapping = new Map<BetTypeGroup, OddsDisplayBlockType>([
    [BetTypeGroup.Basketball_FirstQuarter, OddsDisplayBlockType.FirstQuarter],
    [BetTypeGroup.Basketball_FirstHalfMoneyLine, OddsDisplayBlockType.FirstHalf],
    [BetTypeGroup.AmericanFootball_FirstHalfMoneyLine, OddsDisplayBlockType.FirstHalf],
    [BetTypeGroup.Tennis_GameHandicap, OddsDisplayBlockType.GameHandicap],
    [BetTypeGroup.TableTennis_TotalMatchPoints, OddsDisplayBlockType.TotalMatchPoints],
    [BetTypeGroup.Badminton_TotalMatchPoints, OddsDisplayBlockType.TotalMatchPoints],
    [BetTypeGroup.Volleyball_TotalMatchPoints, OddsDisplayBlockType.TotalMatchPoints],
    [BetTypeGroup.Hockey_FirstPeriod, OddsDisplayBlockType.FirstPeriod],
    [BetTypeGroup.Baseball_FirstInningRuns, OddsDisplayBlockType.FirstInningRuns],
    [BetTypeGroup.Futsal_FirstHalf, OddsDisplayBlockType.FirstHalf],
    [BetTypeGroup.Soccer_PenaltyTenKick, OddsDisplayBlockType.FullTime],
]);

export function getColumnMarketGroups(entry: MarketColumnEntry): BetTypeGroup[] {
    return Array.isArray(entry) ? entry.map(e => e.marketGroup) : [entry.marketGroup];
}

export function getColumnMarketType(entry: MarketColumnEntry): MarketType {
    return Array.isArray(entry) ? entry[0].marketType : entry.marketType;
}

export function getColumnFirstMarketGroup(entry: MarketColumnEntry): BetTypeGroup {
    return Array.isArray(entry) ? entry[0].marketGroup : entry.marketGroup;
}

export function isSingleMode(oddsDisplayMode: OddsDisplayMode): boolean {
    return oddsDisplayMode === OddsDisplayMode.Single;
}

export function getDisplayMarketTypeInfos(sportType: SportType, isLive: boolean, oddsDisplayMode: OddsDisplayMode): MarketColumnEntry[] {
    if (isSingleMode(oddsDisplayMode)) {
        return SingleLineMainMarketConfig[sportType] ?? SingleLineMainMarketConfig.default;
    }

    const configBySportType = DoubleLineMainMarketConfig[sportType] ?? DoubleLineMainMarketConfig.default;

    return configBySportType[isLive ? 'live' : 'nonLive'];
}

export function getNewBetTypeDisplayInfos(
    sportType: SportType,
    betTypeGroup: BetTypeGroup,
    isLive: boolean,
): (MarketColumnEntry | null)[] | undefined {
    const config = DoubleLineMainMarketConfig[sportType] ?? DoubleLineMainMarketConfig.default;
    return isLive ? config.newBetTypeLive?.[betTypeGroup] : undefined;
}

export function getMainMarketGroups(displayMarketTypeInfos: MarketColumnEntry[]) {
    const marketGroups = displayMarketTypeInfos.flatMap(entry => getColumnMarketGroups(entry));
    const hasNextGoal = marketGroups.some(group => isNextGoal(group));
    if (hasNextGoal) {
        marketGroups.push(...nextGoalMarketGroups);
    }

    return removeDuplicates(marketGroups);
}

/**
 * Next goal betTypeGroup is not supported by bet builder right now
 * but because next goal uses handicap market type, so we need to return undefined to skip it
 * if want to show other betTypeGroups, need to handle it in the future because right now there is only marketType to bet builder market type mapping
 */
export function toBetBuilderMarketTypeInfos(displayMarketTypeInfos: MarketColumnEntry[]): (BetBuilderMarketTypeInfo | undefined)[] {
    return displayMarketTypeInfos.map((entry) => {
        const firstMarketGroup = getColumnFirstMarketGroup(entry);
        if (isNextGoal(firstMarketGroup)) {
            return undefined;
        }

        const result = marketTypeToBetBuilder(getColumnMarketType(entry));
        return result ?? undefined;
    });
}

export const MarketTypeNameMapping: Partial<Record<MarketType|MarketTypeHeader, string>> = {
    [MarketType.Handicap]: 'hdp',
    [MarketType.FH_Handicap]: 'hdp',
    [MarketType.OverUnder]: 'ou',
    [MarketType.FH_OverUnder]: 'ou',
    [MarketType._1X2]: '1X2',
    [MarketType.FH_1X2]: '1X2',
    [MarketType.OddEven]: 'oe',
    [MarketType.FH_OddEven]: 'oe',
    [MarketType.TotalGoal]: 'total_goal',
    [MarketType.MoneyLine]: 'ml',
    [MarketType.LiveScore]: 'asian1X2',
    [MarketTypeHeader.More]: 'more',
};

export const MoreMarketTooltipMapping: Partial<Record<MarketType, string>> = {
    [MarketType.Handicap]: 'more_market_hdp_tooltip',
    [MarketType.FH_Handicap]: 'more_market_hdp_tooltip',
    [MarketType.OverUnder]: 'more_market_ou_tooltip',
    [MarketType.FH_OverUnder]: 'more_market_ou_tooltip',
    [MarketType._1X2]: 'more_market_1X2_tooltip',
    [MarketType.FH_1X2]: 'more_market_1X2_tooltip',
    [MarketType.OddEven]: 'more_market_oe_tooltip',
    [MarketType.FH_OddEven]: 'more_market_oe_tooltip',
    [MarketType.TotalGoal]: 'Total Goal_tooltip',
    [MarketType.MoneyLine]: 'more_market_ml_tooltip',
    [MarketType.LiveScore]: 'asian1X2_tooltip',
};

export const HomeOptionTooltipMapping: Partial<Record<MarketType, string>> = {
    [MarketType.Handicap]: 'ft_hdp_home_tooltip',
    [MarketType.FH_Handicap]: 'fh_hdp_home_tooltip',
    [MarketType.OverUnder]: 'ft_ou_over_tooltip',
    [MarketType.FH_OverUnder]: 'fh_ou_over_tooltip',
};

export const AwayOptionTooltipMapping: Partial<Record<MarketType, string>> = {
    [MarketType.Handicap]: 'ft_hdp_away_tooltip',
    [MarketType.FH_Handicap]: 'fh_hdp_away_tooltip',
    [MarketType.OverUnder]: 'ft_ou_under_tooltip',
    [MarketType.FH_OverUnder]: 'fh_ou_under_tooltip',
};

export interface IColumnDescription {
    label: string;
    tooltip: string;
}

export function getMarketColumnDescription(sportType: SportType, marketType: MarketType, marketGroup: BetTypeGroup, isSingleLine: boolean): IColumnDescription {
    if (isHighestOpeningPartnership(marketGroup)) {
        return {
            label: `${i18n.t('odds_header_highest_opening_partnership')} - ${i18n.t('odds_header_1X2')}`,
            tooltip: '',
        };
    }

    if (isNextGoal(marketGroup)) {
        return {
            label: `${i18n.t('odds_header_next_goal')}`,
            tooltip: '',
        };
    }

    const marketTypeKey = MarketTypeNameMapping[marketType] ?? MarketType[marketType];

    if (isSingleLine && isOU(marketType)) {
        const key = marketType === MarketType.OverUnder ? 'ft_goal_tooltip' : 'fh_goal_tooltip';
        return {
            label: i18n.t('odds_header_goal'),
            tooltip: i18n.t(key),
        };
    }

    if (isMobile()) {
        return {
            label: i18n.t(`odds_header_${marketTypeKey}`),
            tooltip: '',
        };
    }

    const isShowSetTitle = isSetGameScoreSport(sportType) && isFullTimeMainMarket(marketType, marketGroup);

    const tooltipKeyPrefix = isShowSetTitle || isFirstHalfBaseballMarket(sportType, marketType, marketGroup) ? `sport_type_${sportType}_` : '';
    const tooltipKey = `odds_header_tooltip_${tooltipKeyPrefix}bet_type_group_${marketGroup}_market_type_${marketType}`;

    return {
        label: i18n.t(`odds_header_${marketTypeKey}`),
        tooltip: i18n.t(tooltipKey),
    };
}

export function getBlockColumnDescription(sportType: SportType, blockType: OddsDisplayBlockType): IColumnDescription {
    let label = i18n.t(`odds_header_${blockType}`);

    if (isSetGameScoreSport(sportType) && blockType === OddsDisplayBlockType.FullTime) {
        label = i18n.t('odds_header_set');
    }

    if (isFirstHalfBaseball(sportType, blockType)) {
        label = i18n.t('odds_header_first5');
    }
    return {
        label,
        tooltip: '',
    };
}

function isSetGameScoreSport(sportType: SportType) {
    const setSportListForOddsDisplay = [SportType.Tennis, SportType.TableTennis, SportType.Volleyball, SportType.Badminton];
    return setSportListForOddsDisplay.includes(sportType);
}
